import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import type { FlightProfile } from "../../core/decorFlights";

const TOP_LANDING = 1;
const STEP_RISE = 0.17;
const STEP_TREAD = 0.3;
const RAIL_HEIGHT = 0.95;
const POST_SPACING = 1.5;
const INNER_RAIL_SPACING = 6;

const concrete = new THREE.MeshStandardMaterial({ name: "concrete", color: "#cfccc5", roughness: 0.95, metalness: 0 });
const nosing = new THREE.MeshStandardMaterial({ name: "nosing", color: "#8f8c86", roughness: 0.9, metalness: 0 });
const pitWall = new THREE.MeshStandardMaterial({ name: "pit", color: "#bcbab5", roughness: 0.95, metalness: 0, side: THREE.DoubleSide });
const rail = new THREE.MeshStandardMaterial({ name: "rail", color: "#2b2e33", roughness: 0.6, metalness: 0.2 });
const curb = new THREE.MeshStandardMaterial({ name: "curb", color: "#dfe2e6", roughness: 0.8, metalness: 0 });
const landing = new THREE.MeshStandardMaterial({ name: "landing", color: "#b7b4ad", roughness: 0.95, metalness: 0 });
const hole = new THREE.MeshBasicMaterial({ name: "mask", color: "#000000" });

function box(width: number, height: number, depth: number, x: number, y: number, z: number, tiltX = 0): THREE.BufferGeometry {
  const geometry = new THREE.BoxGeometry(width, height, depth);
  if (tiltX) geometry.rotateX(tiltX);
  geometry.translate(x, y, z);
  return geometry;
}

function merged(geometries: THREE.BufferGeometry[], material: THREE.Material, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(mergeGeometries(geometries.map((geometry) => geometry.toNonIndexed())), material);
  mesh.name = name;
  return mesh;
}

/** Positions régulières de `from` à `to`, extrémités comprises, espacées d'au plus `spacing`. */
function spaced(from: number, to: number, spacing: number): number[] {
  const count = Math.max(1, Math.ceil(Math.abs(to - from) / spacing));
  return Array.from({ length: count + 1 }, (_, i) => from + ((to - from) * i) / count);
}

/** Dénivelé d'un escalier de cette longueur sans `depth` imposé : une marche standard par giron. */
export function defaultStairsDepth(length: number): number {
  const run = Math.max(STEP_TREAD * 3, length - TOP_LANDING);
  return Math.max(3, Math.round(run / STEP_TREAD)) * STEP_RISE;
}

/** Hauteur de l'escalier le long de sa descente, palier compris : voir `FlightProfile`. */
export function stairsOpeningProfile(length: number, options: StairsOpeningOptions = {}): FlightProfile {
  const run = Math.max(STEP_TREAD * 3, length - TOP_LANDING);
  const steps = Math.max(3, Math.round(options.depth ? options.depth / STEP_RISE : run / STEP_TREAD));
  const rise = options.depth ? options.depth : steps * STEP_RISE;
  return [[0, 0], [TOP_LANDING, 0], [TOP_LANDING + run, -rise]];
}

export interface StairsOpeningOptions {
  /** Dénivelé voulu : le nombre de marches en découle. Absent : une marche par giron standard. */
  depth?: number;
  /** Débouche sur une zone en contrebas : ni paroi ni garde-corps côté bas. */
  openBottom?: boolean;
}

/**
 * Escalier qui descend vers le niveau inférieur, à la largeur et à la longueur voulues : on y entre
 * côté −z par un palier au sol, il plonge vers +z dans une trémie. Suit la convention des trémies
 * du calque (`Mask`, `Pit…`, `Rim`) ; mesures en mètres.
 */
export function buildStairsOpening(width: number, length: number, options: StairsOpeningOptions = {}): THREE.Group {
  const run = Math.max(STEP_TREAD * 3, length - TOP_LANDING);
  const steps = Math.max(3, Math.round(options.depth ? options.depth / STEP_RISE : run / STEP_TREAD));
  const tread = run / steps;
  const stepRise = options.depth ? options.depth / steps : STEP_RISE;
  const rise = steps * stepRise;
  const z0 = -(TOP_LANDING + run) / 2;
  const holeStart = z0 + TOP_LANDING;
  const holeEnd = holeStart + run;
  const holeCenter = (holeStart + holeEnd) / 2;
  const depth = rise + 0.4;

  const root = new THREE.Group();
  const pit = new THREE.Group();
  pit.name = "Pit";

  const blocks: THREE.BufferGeometry[] = [];
  const noses: THREE.BufferGeometry[] = [];
  for (let i = 0; i < steps; i++) {
    const top = -(i + 1) * stepRise;
    const z = holeStart + i * tread;
    blocks.push(box(width, depth + top, tread, 0, (top - depth) / 2, z + tread / 2));
    noses.push(box(width, 0.012, 0.06, 0, top + 0.006, z + 0.03));
  }
  pit.add(merged(blocks, concrete, "PitSteps"), merged(noses, nosing, "PitNosing"));

  const walls: THREE.BufferGeometry[] = [];
  const wall = (w: number, x: number, z: number, rotationY: number) => {
    const plane = new THREE.PlaneGeometry(w, depth);
    plane.rotateY(rotationY);
    plane.translate(x, -depth / 2, z);
    walls.push(plane);
  };
  wall(run, -width / 2, holeCenter, Math.PI / 2);
  wall(run, width / 2, holeCenter, -Math.PI / 2);
  if (!options.openBottom) wall(width, 0, holeEnd, Math.PI);
  pit.add(merged(walls, pitWall, "PitWalls"));

  // Mains courantes le long de la descente : sur les bords et à intervalles réguliers en travers.
  const slope = Math.atan2(rise, run);
  const railLength = Math.hypot(run, rise);
  const railXs = spaced(-width / 2 + 0.15, width / 2 - 0.15, INNER_RAIL_SPACING);
  const inner: THREE.BufferGeometry[] = [];
  for (const x of railXs) {
    inner.push(box(0.06, 0.05, railLength, x, RAIL_HEIGHT - rise / 2, holeCenter, slope));
    for (const z of spaced(holeStart + 0.2, holeEnd - 0.2, POST_SPACING)) {
      const ground = -((z - holeStart) / run) * rise;
      inner.push(box(0.04, RAIL_HEIGHT, 0.04, x, ground + RAIL_HEIGHT / 2, z));
    }
  }
  pit.add(merged(inner, rail, "PitRails"));
  root.add(pit);

  const mask = new THREE.Mesh(new THREE.PlaneGeometry(width, run).rotateX(-Math.PI / 2), hole);
  mask.position.set(0, 0.01, holeCenter);
  mask.name = "Mask";
  root.add(mask);

  // Garde-corps autour de l'ouverture, sauf côté entrée ; palier d'arrivée au sol.
  const rim = new THREE.Group();
  rim.name = "Rim";
  const hx = width / 2 + 0.06;
  const curbs: THREE.BufferGeometry[] = [];
  const rails: THREE.BufferGeometry[] = [];
  for (const sx of [-1, 1]) {
    curbs.push(box(0.12, 0.12, run, sx * hx, 0.06, holeCenter));
    rails.push(box(0.06, 0.05, run, sx * hx, 1.02, holeCenter));
    for (const z of spaced(holeStart, holeEnd, POST_SPACING)) rails.push(box(0.05, 1, 0.05, sx * hx, 0.5, z));
  }
  if (!options.openBottom) {
    curbs.push(box(width + 0.24, 0.12, 0.12, 0, 0.06, holeEnd + 0.06));
    rails.push(box(width + 0.24, 0.05, 0.06, 0, 1.02, holeEnd + 0.06));
    for (const x of spaced(-hx, hx, POST_SPACING)) rails.push(box(0.05, 1, 0.05, x, 0.5, holeEnd + 0.06));
  }
  rim.add(merged(curbs, curb, "RimCurb"), merged(rails, rail, "RimRails"));
  rim.add(merged([box(width, 0.03, TOP_LANDING, 0, 0.015, z0 + TOP_LANDING / 2)], landing, "RimLanding"));
  root.add(rim);

  return root;
}
